import React, { useState, useRef, useEffect } from "react";

const OTPInput = ({ length = 6, onComplete, onChange, error, disabled = false }) => {
  const [otp, setOtp] = useState(Array(length).fill(""));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Call onChange with current OTP string
    const otpString = newOtp.join("");
    if (onChange) {
      onChange(otpString);
    }

    // Move to next input if value entered
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete if all fields filled
    if (otpString.length === length && onComplete) {
      onComplete(otpString);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Only process if pasted data contains only digits
    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const digits = pastedData.slice(0, length).split("");
    const newOtp = [...otp];
    
    digits.forEach((digit, i) => {
      if (i < length) {
        newOtp[i] = digit;
      }
    });

    setOtp(newOtp);

    // Call onChange
    const otpString = newOtp.join("");
    if (onChange) {
      onChange(otpString);
    }

    // Focus last filled input or last input
    const lastFilledIndex = Math.min(digits.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();

    // Call onComplete if all fields filled
    if (otpString.length === length && onComplete) {
      onComplete(otpString);
    }
  };

  const handleFocus = (index) => {
    // Select all text when focusing
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex gap-2 justify-center">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={otp[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            disabled={disabled}
            className={`
              w-12 h-12 text-center text-lg font-semibold
              border-2 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]
              transition-colors
              ${error 
                ? "border-red-500 bg-red-50" 
                : "border-gray-300 bg-white"
              }
              ${disabled ? "bg-gray-100 cursor-not-allowed opacity-60" : ""}
            `}
            aria-label={`OTP digit ${index + 1} of ${length}`}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
};

export default OTPInput;
